import api, {
  authAPI,
  emailPreferencesAPI,
  formsAPI,
  impactplan100PercentAPI,
  impactplanAPI,
  monitorAPI,
  packageAPI,
  PageSummaryPayload,
  PageSummarySection,
  powerbiAPI,
  usage100PercentAPI,
  xeroAPI,
} from '../services/api';
type Viewer = {
  name: string | null;
  role: string | null;
};

type RouteMatch = {
  params: Record<string, string>;
};

export interface AuraLensRouteConfig {
  pageKey: string;
  mode: 'structured' | 'generic';
  match: (pathname: string) => RouteMatch | null;
  buildPayload: (args: { pathname: string; params: Record<string, string>; viewer: Viewer }) => Promise<PageSummaryPayload>;
}

const exact = (expected: string) => (pathname: string): RouteMatch | null =>
  pathname === expected ? { params: {} } : null;

const formWorkspaceMatch = (pathname: string): RouteMatch | null => {
  const match = pathname.match(/^\/forms\/([^/]+)\/workspace$/);
  return match ? { params: { id: match[1] } } : null;
};

function formatRoleLabel(role?: string | null): string {
  if (!role) return 'team member';
  return String(role)
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function buildDefaultPersonalisation(viewer: Viewer, page: string, extra?: string): string | null {
  if (!viewer.name) {
    return extra || null;
  }

  const pageLabel = page.replace(/[_-]+/g, ' ');
  const roleLabel = formatRoleLabel(viewer.role);

  if (extra) {
    return `${viewer.name} is signed in as ${roleLabel}. ${extra}`;
  }

  return `${viewer.name} is signed in as ${roleLabel}. End with a short encouraging line that helps them make progress on the ${pageLabel} page.`;
}

function createPayload(page: string, viewer: Viewer, sections: PageSummarySection[]): PageSummaryPayload {
  return {
    page,
    viewer,
    generatedAt: new Date().toISOString(),
    personalisation: {
      message: buildDefaultPersonalisation(viewer, page),
    },
    sections,
  };
}

function createHeroSection(title: string, description: string, stats: Array<{ label: string; value?: string | number }>): PageSummarySection {
  return {
    key: 'hero-overview',
    title: 'Hero overview',
    data: {
      title,
      description,
      stats,
    },
  };
}

function normalizeForms(data: any): any[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (data?.data && Array.isArray(data.data)) {
    return data.data;
  }
  return [];
}

async function buildDashboardPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  const payload = await monitorAPI.getPageSummaryPayload('dashboard');
  return {
    ...payload,
    personalisation: payload.personalisation || {
      message: buildDefaultPersonalisation(
        viewer,
        'dashboard',
        'Add a short personalised line that recognises the user’s current operational view and encourages steady follow-through on alerts and customer actions.'
      ),
    },
  };
}

async function buildFormsPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  const response = await formsAPI.getFormsPage({ page: 1, limit: 100 });
  const forms = normalizeForms(response);
  const totalRecords = Number(response?.pagination?.total || forms.length);
  const active = forms.filter((form) => form.isActive).length;
  const atCap = forms.filter((form) => Number(form.usagePercentage || 0) >= 100).length;
  const nearCap = forms.filter((form) => Number(form.usagePercentage || 0) >= 75 && Number(form.usagePercentage || 0) < 100).length;
  const ownedForms = viewer.name
    ? forms.filter((form) => String(form.owner?.username || '').toLowerCase() === String(viewer.name).toLowerCase())
    : [];
  const activeOwnedForms = ownedForms.filter((form) => form.isActive).length;
  const payload = createPayload('forms', viewer, [
    createHeroSection('Pool Customers', 'Service forms, assignment coverage, and usage risk across the main customer base.', [
      { label: 'Records', value: totalRecords },
      { label: 'Loaded now', value: forms.length },
      { label: 'Active', value: active },
      { label: 'Near cap', value: nearCap },
      { label: 'At cap', value: atCap },
    ]),
    {
      key: 'risk-snapshot',
      title: 'Risk snapshot',
      data: {
        totalRecords,
        loadedRecords: forms.length,
        nearCap,
        atCap,
        inactive: forms.filter((form) => !form.isActive).length,
      },
    },
    {
      key: 'top-customers',
      title: 'Top customers',
      data: {
        totalRecords,
        loadedRecords: forms.length,
        items: [...forms]
          .sort((left, right) => Number(right.usagePercentage || 0) - Number(left.usagePercentage || 0))
          .slice(0, 5)
          .map((form) => ({
            customerName: form.customerName,
            serviceLine: form.serviceLine,
            usagePercentage: Number(form.usagePercentage || 0),
            accountManager: form.accountManager || 'Unassigned',
          })),
      },
    },
  ]);

  return {
    ...payload,
    personalisation: {
      message: buildDefaultPersonalisation(
        viewer,
        'Pool Customers',
        viewer.name && ownedForms.length > 0
          ? `${viewer.name} currently has ${ownedForms.length} loaded form${ownedForms.length === 1 ? '' : 's'} in their name here, with ${activeOwnedForms} active, from the first visible batch of records. Recognise that contribution and encourage them to keep adding and maintaining strong customer records.`
          : 'If the user has no forms in their own name in the currently loaded batch, encourage them to start building and maintaining more customer records here.'
      ),
    },
  };
}

async function buildFormWorkspacePayload(viewer: Viewer, id: string): Promise<PageSummaryPayload> {
  const data = await formsAPI.getWorkspace(id);
  const { form, notifications, operationalState, notes, recentEvents, actionSummary, emailHistory, relatedForms } = data;

  return createPayload('forms-workspace', viewer, [
    createHeroSection(
      form?.customerName || 'Workspace',
      'Single service-line workspace covering operational state, notes, events, and communications.',
      [
        { label: 'Service line', value: form?.serviceLine || 'Unknown' },
        { label: 'Status', value: form?.status || 'Unknown' },
        { label: 'Usage %', value: `${Number(form?.usagePercentage || 0).toFixed(1)}%` },
      ]
    ),
    {
      key: 'operational-state',
      title: 'Operational state',
      data: {
        state: operationalState || {},
        notesCount: notes?.length || 0,
        relatedForms: relatedForms?.length || 0,
      },
    },
    {
      key: 'activity-summary',
      title: 'Activity summary',
      data: {
        recentEvents: (recentEvents || []).slice(0, 5),
        actionSummary: (actionSummary || []).slice(0, 5),
      },
    },
    {
      key: 'communications',
      title: 'Communications',
      data: {
        notifications: (notifications || []).slice(0, 5),
        emailHistory: (emailHistory || []).slice(0, 5),
      },
    },
  ]);
}

async function buildUsersPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  const users = await authAPI.getAllUsers();
  return createPayload('users', viewer, [
    createHeroSection('User Management', 'Identity, role assignment, verification, and access management.', [
      { label: 'Users', value: users.length },
      { label: 'Active', value: users.filter((user: any) => user.isActive && user.emailVerified).length },
      { label: 'Admins', value: users.filter((user: any) => user.hasAdminAccess).length },
    ]),
    {
      key: 'identity-status',
      title: 'Identity status',
      data: {
        unverified: users.filter((user: any) => !user.emailVerified).length,
        inactive: users.filter((user: any) => !user.isActive).length,
        roles: users.reduce((acc: Record<string, number>, user: any) => {
          acc[user.role] = (acc[user.role] || 0) + 1;
          return acc;
        }, {}),
      },
    },
  ]);
}

async function buildUsage100Payload(viewer: Viewer): Promise<PageSummaryPayload> {
  const [forms, stats] = await Promise.all([
    usage100PercentAPI.getAll(),
    usage100PercentAPI.getStatistics(),
  ]);

  return createPayload('usage-100-percent', viewer, [
    createHeroSection('100% Usage', 'Critical service lines that have reached or exceeded package limits.', [
      { label: 'Records', value: stats?.total || forms.length },
      { label: 'Active', value: stats?.active || 0 },
      { label: 'This month', value: stats?.thisMonth || 0 },
    ]),
    {
      key: 'critical-records',
      title: 'Critical records',
      data: {
        items: (forms || []).slice(0, 5).map((form: any) => ({
          customerName: form.customerName,
          serviceLine: form.serviceLine,
          usagePercentage: Number(form.usagePercentage || 0),
          reachedAt: form.reachedAt || null,
        })),
      },
    },
  ]);
}

async function buildServiceAccountsPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  const response = await api.get('/api/service-accounts?page=1&limit=100');
  const accounts = response.data?.data || [];

  return createPayload('service-accounts', viewer, [
    createHeroSection('Service Accounts', 'Machine credentials, scopes, and rate-limited API access.', [
      { label: 'Accounts', value: accounts.length },
      { label: 'Active', value: accounts.filter((account: any) => account.isActive).length },
    ]),
    {
      key: 'account-health',
      title: 'Account health',
      data: {
        recentlyUsed: accounts.filter((account: any) => Boolean(account.lastUsedAt)).slice(0, 5),
        inactive: accounts.filter((account: any) => !account.isActive).length,
      },
    },
  ]);
}

async function buildAuditPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  const response = await formsAPI.getAllAudits();
  const logs = response?.data || [];
  return createPayload('audit', viewer, [
    createHeroSection('Audit Trail', 'Field-level service form history for review and compliance.', [
      { label: 'Records', value: logs.length },
      { label: 'Distinct fields', value: new Set(logs.map((log: any) => log.fieldName)).size },
    ]),
    {
      key: 'recent-audit',
      title: 'Recent audit',
      data: {
        items: logs.slice(0, 5).map((log: any) => ({
          timestamp: log.timestamp,
          user: log.user ? `${log.user.username} (${log.user.email})` : 'System',
          fieldName: log.fieldName,
          serviceLine: log.form?.serviceLine || 'Unknown',
        })),
      },
    },
  ]);
}

async function buildUsagePayload(viewer: Viewer): Promise<PageSummaryPayload> {
  const [summaryCards, currentMonth, history] = await Promise.allSettled([
    monitorAPI.getSummaryCards(),
    monitorAPI.getCurrentMonthTotal(),
    monitorAPI.getUsageHistory(6),
  ]);

  const cards = summaryCards.status === 'fulfilled' ? summaryCards.value : { totalUsageGB: 0, totalCustomers: 0, currentMonthUsageGB: 0 };
  const month = currentMonth.status === 'fulfilled' ? currentMonth.value : null;
  const usageHistory = history.status === 'fulfilled' ? history.value : [];

  return createPayload('usage', viewer, [
    createHeroSection('Data Usage', 'Usage aggregates, current-month totals, and historical patterns.', [
      { label: 'Total usage GB', value: Number(cards.totalUsageGB || 0).toFixed(1) },
      { label: 'Customers', value: cards.totalCustomers || 0 },
      { label: 'Current month GB', value: Number(cards.currentMonthUsageGB || 0).toFixed(1) },
    ]),
    {
      key: 'current-period',
      title: 'Current period',
      data: month || {},
    },
    {
      key: 'usage-history',
      title: 'Usage history',
      data: {
        months: (usageHistory || []).slice(0, 6),
      },
    },
  ]);
}

async function buildImpactPlanPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  const [status, forms] = await Promise.allSettled([
    impactplanAPI.getStatus(),
    impactplanAPI.getForms(),
  ]);
  const monitorStatus = status.status === 'fulfilled' ? status.value : {};
  const formList = forms.status === 'fulfilled' ? forms.value || [] : [];

  return createPayload('impact-plan', viewer, [
    createHeroSection('Impact Plan', 'Impact Plan forms, monitor state, and mapped customer service lines.', [
      { label: 'Forms', value: formList.length },
      { label: 'Monitor', value: monitorStatus?.running ? 'Running' : 'Stopped' },
      { label: 'Active', value: formList.filter((form: any) => form.isActive).length },
    ]),
    {
      key: 'impact-usage',
      title: 'Impact usage',
      data: {
        highRisk: formList.filter((form: any) => Number(form.usagePercentage || 0) >= 75).length,
        sample: formList.slice(0, 5).map((form: any) => ({
          customerName: form.customerName || form.nickname || 'Unmapped customer',
          serviceLine: form.serviceLine,
          usagePercentage: Number(form.usagePercentage || 0),
        })),
      },
    },
  ]);
}

async function buildPowerBIPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  const [status, tables] = await Promise.allSettled([
    powerbiAPI.getStatus(),
    powerbiAPI.getTables(),
  ]);
  const monitorStatus = status.status === 'fulfilled' ? status.value : {};
  const tableList = tables.status === 'fulfilled' ? tables.value?.tables || [] : [];

  return createPayload('powerbi', viewer, [
    createHeroSection('Power BI', 'Power BI monitor status, table inventory, and scheduled collection state.', [
      { label: 'Running', value: monitorStatus?.isRunning ? 'Yes' : 'No' },
      { label: 'Scheduled', value: monitorStatus?.isScheduled ? 'Yes' : 'No' },
      { label: 'Tables', value: tableList.length },
    ]),
    {
      key: 'table-summary',
      title: 'Table summary',
      data: {
        items: tableList.slice(0, 5),
        lastResult: monitorStatus?.lastResult || null,
      },
    },
  ]);
}

async function buildXeroPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  const status = await xeroAPI.getStatus().catch(() => ({}));

  return createPayload('xero', viewer, [
    createHeroSection('Xero Monitor', 'Xero monitor state and connection activity.', [
      { label: 'Running', value: status?.isRunning ? 'Yes' : 'No' },
      { label: 'Scheduled', value: status?.isScheduled ? 'Yes' : 'No' },
      { label: 'Last result', value: status?.lastResult?.success ? 'Healthy' : status?.lastResult ? 'Needs attention' : 'Unknown' },
    ]),
    {
      key: 'xero-status',
      title: 'Xero status',
      data: status || {},
    },
  ]);
}

async function buildSalesPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  const forms = normalizeForms(await formsAPI.getAllForms());
  return createPayload('sales', viewer, [
    createHeroSection('Sales', 'Account-manager sales view over assigned customers and usage posture.', [
      { label: 'Records', value: forms.length },
      { label: 'Opted in', value: forms.filter((form) => String(form.status || '').toLowerCase() === 'opted in').length },
      { label: 'High usage', value: forms.filter((form) => Number(form.usagePercentage || 0) >= 75).length },
    ]),
    {
      key: 'sales-summary',
      title: 'Sales summary',
      data: {
        sample: forms.slice(0, 5).map((form) => ({
          customerName: form.customerName,
          serviceLine: form.serviceLine,
          accountManager: form.accountManager || 'Unassigned',
        })),
      },
    },
  ]);
}

async function buildMonitorPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  const [status, logs] = await Promise.allSettled([
    monitorAPI.getStatus(),
    monitorAPI.getLogs(50),
  ]);

  const monitorStatus = status.status === 'fulfilled' ? status.value : {};
  const logLines = logs.status === 'fulfilled' ? logs.value || [] : [];

  return createPayload('monitor', viewer, [
    createHeroSection('Starlink Monitor', 'Live monitor controls, latest output, and notification settings.', [
      { label: 'Running', value: monitorStatus?.running ? 'Yes' : 'No' },
      { label: 'State', value: monitorStatus?.state || 'Unknown' },
      { label: 'Recent logs', value: logLines.length },
    ]),
    {
      key: 'monitor-summary',
      title: 'Monitor summary',
      data: {
        status: monitorStatus,
        latestLog: logLines.length > 0 ? logLines[logLines.length - 1] : null,
      },
    },
  ]);
}

async function buildPackagesPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  const [packagesData, formsData] = await Promise.all([
    packageAPI.list(),
    formsAPI.getAllForms().catch(() => ({ data: [] })),
  ]);
  const packages = Array.isArray(packagesData) ? packagesData : packagesData?.data || [];
  const forms = normalizeForms(formsData);

  return createPayload('packages', viewer, [
    createHeroSection('Packages', 'Capped data package definitions and their usage across forms.', [
      { label: 'Packages', value: packages.length },
      { label: 'Forms', value: forms.length },
    ]),
    {
      key: 'package-summary',
      title: 'Package summary',
      data: {
        packages: packages.slice(0, 5),
        topMatches: forms.slice(0, 5).map((form) => ({
          customerName: form.customerName,
          packageLimit: form.packageLimit,
        })),
      },
    },
  ]);
}

async function buildEmailManagementPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  const users = await emailPreferencesAPI.getAllUsersWithPreferences();

  return createPayload('email-management', viewer, [
    createHeroSection('Email preferences', 'Notification settings by user for monitor, usage, and account events.', [
      { label: 'Users', value: users.length },
      { label: 'Verified', value: users.filter((user: any) => user.emailVerified).length },
    ]),
    {
      key: 'preference-summary',
      title: 'Preference summary',
      data: {
        users: users.slice(0, 5).map((user: any) => ({
          username: user.username,
          email: user.email,
          role: user.role,
        })),
      },
    },
  ]);
}

async function buildDataTransferPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  return createPayload('data-transfer', viewer, [
    createHeroSection('Data Transfer', 'Transfer data between service forms that belong to the same customer identifier.', [
      { label: 'Mode', value: 'Customer-to-customer transfer workflow' },
    ]),
    {
      key: 'transfer-workflow',
      title: 'Transfer workflow',
      data: {
        steps: [
          'Search by customer ID',
          'Select source service',
          'Select destination service',
          'Choose transfer amount',
        ],
      },
    },
  ]);
}

async function buildImpactPlanDetailsPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  const [forms, stats] = await Promise.all([
    impactplan100PercentAPI.getAll(),
    impactplan100PercentAPI.getStatistics(),
  ]);

  return createPayload('impact-plan-details', viewer, [
    createHeroSection('Impact Plan Details', 'Impact Plan 100% usage records and critical exceptions.', [
      { label: 'Records', value: stats?.total || forms.length },
      { label: 'Active', value: stats?.active || 0 },
    ]),
    {
      key: 'impact-plan-critical',
      title: 'Impact plan critical records',
      data: {
        items: (forms || []).slice(0, 5).map((form: any) => ({
          customerName: form.customerName || 'Unknown',
          serviceLine: form.serviceLineNumber,
          usagePercentage: Number(form.usagePercentage || 0),
        })),
      },
    },
  ]);
}

async function buildAuthenticationPayload(viewer: Viewer): Promise<PageSummaryPayload> {
  return createPayload('authentication', viewer, [
    createHeroSection('Authentication Management', 'Authentication endpoints and user-management capabilities placeholder page.', [
      { label: 'State', value: 'Placeholder' },
    ]),
    {
      key: 'authentication-summary',
      title: 'Authentication summary',
      data: {
        message: 'Authentication endpoints and user management will be implemented here.',
      },
    },
  ]);
}

const routeConfigs: AuraLensRouteConfig[] = [
  { pageKey: 'dashboard', mode: 'structured', match: exact('/dashboard'), buildPayload: async ({ viewer }) => buildDashboardPayload(viewer) },
  { pageKey: 'forms', mode: 'structured', match: exact('/forms'), buildPayload: async ({ viewer }) => buildFormsPayload(viewer) },
  { pageKey: 'forms-workspace', mode: 'structured', match: formWorkspaceMatch, buildPayload: async ({ viewer, params }) => buildFormWorkspacePayload(viewer, params.id) },
  { pageKey: 'sales', mode: 'generic', match: exact('/sales'), buildPayload: async ({ viewer }) => buildSalesPayload(viewer) },
  { pageKey: 'usage', mode: 'structured', match: exact('/usage'), buildPayload: async ({ viewer }) => buildUsagePayload(viewer) },
  { pageKey: 'users', mode: 'structured', match: exact('/users'), buildPayload: async ({ viewer }) => buildUsersPayload(viewer) },
  { pageKey: 'monitor', mode: 'generic', match: exact('/monitor'), buildPayload: async ({ viewer }) => buildMonitorPayload(viewer) },
  { pageKey: 'packages', mode: 'generic', match: exact('/packages'), buildPayload: async ({ viewer }) => buildPackagesPayload(viewer) },
  { pageKey: 'audit', mode: 'structured', match: exact('/audit'), buildPayload: async ({ viewer }) => buildAuditPayload(viewer) },
  { pageKey: 'powerbi', mode: 'structured', match: exact('/powerbi'), buildPayload: async ({ viewer }) => buildPowerBIPayload(viewer) },
  { pageKey: 'xero', mode: 'structured', match: exact('/xero'), buildPayload: async ({ viewer }) => buildXeroPayload(viewer) },
  { pageKey: 'email-management', mode: 'generic', match: exact('/email-management'), buildPayload: async ({ viewer }) => buildEmailManagementPayload(viewer) },
  { pageKey: 'usage-100-percent', mode: 'structured', match: exact('/usage-100-percent'), buildPayload: async ({ viewer }) => buildUsage100Payload(viewer) },
  { pageKey: 'data-transfer', mode: 'generic', match: exact('/data-transfer'), buildPayload: async ({ viewer }) => buildDataTransferPayload(viewer) },
  { pageKey: 'impact-plan', mode: 'structured', match: exact('/impact-plan'), buildPayload: async ({ viewer }) => buildImpactPlanPayload(viewer) },
  { pageKey: 'impact-plan-details', mode: 'generic', match: exact('/impact-plan/details'), buildPayload: async ({ viewer }) => buildImpactPlanDetailsPayload(viewer) },
  { pageKey: 'service-accounts', mode: 'structured', match: exact('/service-accounts'), buildPayload: async ({ viewer }) => buildServiceAccountsPayload(viewer) },
  { pageKey: 'authentication', mode: 'generic', match: exact('/auth'), buildPayload: async ({ viewer }) => buildAuthenticationPayload(viewer) },
];

export function getAuraLensRouteMatch(pathname: string): { config: AuraLensRouteConfig; params: Record<string, string> } | null {
  for (const config of routeConfigs) {
    const match = config.match(pathname);
    if (match) {
      return { config, params: match.params };
    }
  }

  return null;
}
